<template>
  <div class="ocr-models">
    <div class="ocr-models-header">
      <div class="header-left">
        <router-link to="/integrations/page/protokol-media/page" class="back-link">
          <i class="fa-solid fa-arrow-left"></i>
        </router-link>
        <h2 class="header-title">{{ $t('integrations.media.ocr.title') }}</h2>
      </div>
      <button class="btn-create" @click="$router.push('/integrations/page/protokol-media/ocr-editor')">
        <i class="fa-solid fa-plus"></i>
        {{ $t('integrations.media.ocr.createModel') }}
      </button>
    </div>

    <div v-if="loading" class="ocr-models-loading">
      <i class="fa-solid fa-spinner fa-spin"></i>
    </div>

    <div v-else-if="models.length === 0" class="ocr-models-empty">
      <div class="empty-icon">
        <i class="fa-solid fa-crop-simple"></i>
      </div>
      <h3>{{ $t('integrations.media.ocr.noModels') }}</h3>
      <p>{{ $t('integrations.media.ocr.noModelsDesc') }}</p>
      <button class="btn-create" @click="$router.push('/integrations/page/protokol-media/ocr-editor')">
        <i class="fa-solid fa-plus"></i>
        {{ $t('integrations.media.ocr.createFirst') }}
      </button>
    </div>

    <div v-else class="ocr-models-grid">
      <div
        v-for="model in models"
        :key="model.uuid"
        class="model-card"
      >
        <div class="model-card-header">
          <h3 class="model-name">{{ model.name }}</h3>
          <span class="engine-badge" :class="'engine-badge--' + model.engine">
            {{ model.engine === 'kortex' ? $t('integrations.media.ocr.engineKortex') : $t('integrations.media.ocr.engineDefault') }}
          </span>
        </div>
        <p v-if="model.description" class="model-desc">{{ model.description }}</p>
        <div v-if="model.language" class="model-langs">
          <span class="model-lang-tag">{{ langName(model.language) }}</span>
        </div>
        <div class="model-stats">
          <span class="stat">
            <i class="fa-solid fa-vector-square"></i>
            {{ (model.regions && model.regions.length) || 0 }} {{ $t('integrations.media.ocr.regions') }}
          </span>
          <span class="stat">
            <i class="fa-solid fa-image"></i>
            {{ (model.samples && model.samples.length) || 0 }} {{ $t('integrations.media.ocr.samples') }}
          </span>
        </div>
        <div class="model-actions">
          <button class="btn-action btn-edit" @click="editModel(model)">
            <i class="fa-solid fa-pen"></i>
            {{ $t('integrations.media.ocr.edit') }}
          </button>
          <button class="btn-action btn-extract" @click="extractModel(model)">
            <i class="fa-solid fa-magnifying-glass"></i>
            {{ $t('integrations.media.ocr.extract') }}
          </button>
          <button class="btn-action btn-delete" @click="confirmDelete(model)">
            <i class="fa-solid fa-trash"></i>
          </button>
        </div>
      </div>
    </div>

  </div>
</template>

<script>
import { getLanguageName } from './ocrLanguages'

export default {
  props: {
    context: { type: Object },
  },

  computed: {
    models() {
      return this.$store.getters['ocrModels/models']
    },
    loading() {
      return this.$store.getters['ocrModels/loading']
    },
  },

  async mounted() {
    const plan = await this.$store.dispatch('ocrModels/fetchPlan')
    if (!plan || plan.plan !== 'paid') {
      this.$router.replace('/integrations/page/protokol-media/page')
      return
    }
    this.$store.dispatch('ocrModels/fetchModels')
  },

  methods: {
    editModel(model) {
      this.$router.push(`/integrations/page/protokol-media/ocr-editor?uuid=${model.uuid}`)
    },

    extractModel(model) {
      this.$router.push(`/integrations/page/protokol-media/ocr-extract?uuid=${model.uuid}`)
    },

    async confirmDelete(model) {
      const confirmed = await this.$mainDialog.msgBoxConfirm(`Delete "${model.name}"? This cannot be undone.`)
      if (confirmed) {
        await this.$store.dispatch('ocrModels/deleteModel', model.uuid)
      }
    },

    langName(code) {
      return getLanguageName(code)
    },
  },
}
</script>

<style scoped lang="scss">
@import "@/assets/scss/_custom.scss";

.ocr-models {
  padding: 32px;
  min-height: 100%;
}

.ocr-models-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 28px;
}

.header-left {
  display: flex;
  align-items: center;
  gap: 16px;
}

.back-link {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  color: var(--text-muted);
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  text-decoration: none;
  transition: all 0.15s ease;

  &:hover {
    color: var(--text-primary);
    border-color: var(--border-hover);
  }
}

.header-title {
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--text-primary);
  margin: 0;
}

.btn-create {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 20px;
  background: linear-gradient(135deg, #10b981, #34d399);
  color: #fff;
  border: none;
  border-radius: 10px;
  font-weight: 600;
  font-size: 0.9rem;
  cursor: pointer;
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 6px 20px rgba(16, 185, 129, 0.35);
  }
}

.ocr-models-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 80px 0;
  color: var(--text-muted);
  font-size: 1rem;
}

.ocr-models-empty {
  text-align: center;
  padding: 80px 20px;
  color: var(--text-muted);

  .empty-icon {
    font-size: 3rem;
    margin-bottom: 16px;
    opacity: 0.3;
  }

  h3 {
    color: var(--text-primary);
    margin: 0 0 8px;
  }

  p {
    margin: 0 0 24px;
    max-width: 400px;
    margin-left: auto;
    margin-right: auto;
  }
}

.ocr-models-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
  gap: 16px;
}

.model-card {
  background: var(--bg-card);
  border: 1px solid var(--border-default);
  border-radius: 14px;
  padding: 20px;
  transition: border-color 0.15s ease;

  &:hover {
    border-color: var(--border-hover);
  }
}

.model-card-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.model-name {
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.engine-badge {
  padding: 3px 10px;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.engine-badge--default {
  background: rgba(102, 126, 234, 0.15);
  color: #667eea;
}

.engine-badge--kortex {
  background: rgba(139, 92, 246, 0.15);
  color: #8b5cf6;
}

.model-desc {
  font-size: 0.85rem;
  color: var(--text-muted);
  margin: 0 0 12px;
  line-height: 1.5;
}

.model-langs {
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
  margin-bottom: 12px;
}

.model-lang-tag {
  padding: 2px 7px;
  background: rgba(102, 126, 234, 0.1);
  color: #667eea;
  border-radius: 4px;
  font-size: 0.7rem;
  font-weight: 500;
}

.model-stats {
  display: flex;
  gap: 16px;
  margin-bottom: 16px;

  .stat {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 0.8rem;
    color: var(--text-muted);
  }
}

.model-actions {
  display: flex;
  gap: 8px;
}

.btn-action {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border: 1px solid var(--border-default);
  border-radius: 8px;
  background: transparent;
  color: var(--text-muted);
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    color: var(--text-primary);
    border-color: var(--border-hover);
  }
}

.btn-delete:hover {
  color: #ef4444;
  border-color: rgba(239, 68, 68, 0.3);
}

.btn-extract {
  margin-left: auto;
}

</style>
